import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiService, getApiBaseUrl, type AuthResponse, type User } from '../services/apiService';
import PasswordInput from '../components/PasswordInput';
import { clearAuthAndTenantStorage } from '../utils/authStorage';
import { redirectToTenant, isLocalDevHostname, getHostTenantSlug } from '../utils/tenantResolver';
import {
  isSuperAdminUser,
  isSuperAdminTenantSlug,
  SUPER_ADMIN_ACTIVITY_HOME,
  SUPER_ADMIN_TENANT_SLUG,
} from '../utils/superAdmin';
import { useTestAuthFlow } from '../hooks/useTestAuthFlow';
import { PASSWORD_REQUIREMENTS_HINT, PASSWORD_MAX_LENGTH } from '../utils/passwordPolicy';
import {
  captureOnboardingFromUrl,
  isOnboardingSessionActive,
} from '../utils/onboardingStorage';
import AuthPageTemplate from '../components/auth/AuthPageTemplate';
import '../styles/Auth.css';

/** In-flight exchange requests (StrictMode-safe: second mount awaits the same promise, never stale localStorage). */
const exchangeTokenInflight = new Map<string, Promise<AuthResponse>>();

interface LoginForm {
  email: string;
  password: string;
}

interface LoginErrors extends Partial<LoginForm> {
  otp?: string;
  captcha?: string;
}

const LOGIN_ATTEMPT_AT_KEY = 'login:attempt-at';
const PASSWORD_RESET_LOGIN_QUERY = 'passwordReset';

const markLoginAttempt = () => {
  try {
    sessionStorage.setItem(LOGIN_ATTEMPT_AT_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
};

const isRecentLoginAttempt = (): boolean => {
  try {
    const attemptAt = Number(sessionStorage.getItem(LOGIN_ATTEMPT_AT_KEY));
    return Number.isFinite(attemptAt) && Date.now() - attemptAt <= 60_000;
  } catch {
    return false;
  }
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { testAuthFlowEnabled, testStaticCaptcha, testStaticOtp } = useTestAuthFlow();
  const { login, logout, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isMainDomain, tenantSlug, setOrganization } = useTenant();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<LoginErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  /** True on first paint when URL carries exchangeToken — avoids one frame of the login form before the effect runs. */
  const [isProcessingAuth, setIsProcessingAuth] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return Boolean(new URLSearchParams(window.location.search).get('exchangeToken'));
    } catch {
      return false;
    }
  });
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionReplacedNotice] = useState(false);
  const captchaCanvasRef = useRef<HTMLCanvasElement>(null);
  const loginSubmitInFlightRef = useRef(false);
  const otpSendInFlightRef = useRef(false);
  /** Keeps "Signing you in…" visible until navigation finishes (avoids a second login prompt). */
  const postLoginRedirectRef = useRef(false);
  /** Prevent duplicate auto-redirect runs from triggering login/handoff loops. */
  const autoRedirectInFlightRef = useRef(false);

  /** Marks an in-flight post-login redirect without showing the signing UI (main domain → tenant handoff). */
  const markPostLoginHandoff = () => {
    postLoginRedirectRef.current = true;
    try {
      // Long enough for AuthProvider bootstrap (fetch orgs + /user/me) after full-page redirect.
      sessionStorage.setItem('auth_handoff_until', String(Date.now() + 45000));
    } catch {
      /* ignore */
    }
  };

  const beginPostLoginRedirect = () => {
    markPostLoginHandoff();
    flushSync(() => {
      setIsProcessingAuth(true);
      setIsLoading(true);
    });
  };

  const isExchangeHandoffInProgress = (): boolean => {
    try {
      return Boolean(new URLSearchParams(window.location.search).get('exchangeToken'));
    } catch {
      return false;
    }
  };

  const isSuperAdminEmail = isSuperAdminUser({ email: formData.email.trim() });
  const currentHost = window.location.hostname.split(':')[0].toLowerCase();
  const shouldUseTenantHandoff = isMainDomain || currentHost === 'app.localhost';

  // Helper function to determine redirect path based on user role / super-admin
  const getRedirectPath = (user?: { role?: string; email?: string }): string => {
    if (user?.email && isSuperAdminUser(user)) {
      return SUPER_ADMIN_ACTIVITY_HOME;
    }
    if (user?.role === 'ADMIN') {
      return '/manage-account?tab=dashboard';
    }
    return '/dashboard';
  };

  const resolvePostLoginPath = (
    rawReturnUrl: string | null,
    user?: { role?: string; email?: string },
    fallbackOverride?: string
  ): string => {
    const fallbackPath = fallbackOverride ?? getRedirectPath(user);
    if (!rawReturnUrl) {
      return fallbackPath;
    }
    const trimmed = rawReturnUrl.trim();
    if (!trimmed || !trimmed.startsWith('/')) {
      return fallbackPath;
    }
    const lower = trimmed.toLowerCase();
    if (lower === '/login' || lower.startsWith('/login?') || lower === '/register' || lower.startsWith('/register?') || lower === '/forgot-password' || lower.startsWith('/forgot-password?')) {
      return fallbackPath;
    }
    return trimmed;
  };
  // comment added
  //comment added

  /**
   * Cross-subdomain JWT handoff must land on /login so this page's exchange effect runs.
   * (Redirecting to /dashboard?exchangeToken=… never mounts Login — user had to sign in twice.)
   */
  const buildTenantExchangeHandoffPath = (postExchangePath: string): string => {
    const params = new URLSearchParams();
    params.set('returnUrl', postExchangePath);
    if (isOnboardingSessionActive()) {
      params.set('onboarding', '1');
    }
    return `/login?${params.toString()}`;
  };

  /** Main-domain handoff failed — stay on login (marketing host has no /dashboard route). */
  const failMainDomainHandoff = (text: string) => {
    postLoginRedirectRef.current = false;
    setIsProcessingAuth(false);
    setIsLoading(false);
    setMessage({ text, type: 'error' });
  };

  /** Full reload so AuthProvider + ProtectedRoute read session from storage (avoids SPA race back to /login). */
  const replaceLocationToAppPath = (path: string) => {
    const url = new URL(path, window.location.origin);
    window.location.replace(url.href);
  };

  /** Persist session for tenant host without React login() — avoids ProjectProvider API calls on /login before navigation. */
  const writeTenantSessionToStorage = (user: User, token: string) => {
    const hostSlugRaw = getHostTenantSlug();
    const hostSlug =
      hostSlugRaw && !isSuperAdminTenantSlug(hostSlugRaw) ? hostSlugRaw : undefined;
    const orgSlug = user.organizationSlug || hostSlug;
    const enrichedUser: User = { ...user };
    if (orgSlug) {
      enrichedUser.organizationSlug = orgSlug;
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(enrichedUser));
    if (orgSlug && enrichedUser.organizationId) {
      localStorage.setItem(
        'currentOrganization',
        JSON.stringify({
          id: enrichedUser.organizationId,
          name: '',
          slug: orgSlug,
          planType: enrichedUser.planType || 'FREE',
        })
      );
    }
  };

  const workspaceSlugMatchesHost = (user: User, hostSlug: string): boolean =>
    String(user.organizationSlug || '').toLowerCase() === hostSlug;

  const primeStorageForHostWorkspace = (token: string, user: User, hostSlug: string) => {
    const primed: User = {
      ...user,
      organizationSlug: user.organizationSlug || hostSlug,
    };
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(primed));
    return primed;
  };

  /**
   * Align JWT/user with the subdomain in the URL.
   * Only calls switchOrganization when the org slug differs — avoids extra API round-trips that caused 401 → login loops.
   */
  const bindWorkspaceSessionIfNeeded = async (
    token: string,
    user: User
  ): Promise<{ token: string; user: User }> => {
    const hostSlug = getHostTenantSlug();
    if (isMainDomain || !hostSlug || isSuperAdminTenantSlug(hostSlug)) {
      return { token, user };
    }

    if (workspaceSlugMatchesHost(user, hostSlug)) {
      const enriched: User = {
        ...user,
        organizationSlug: user.organizationSlug || hostSlug,
      };
      if (enriched.organizationId) {
        return { token, user: enriched };
      }
      const primed = primeStorageForHostWorkspace(token, enriched, hostSlug);
      const orgs = await fetchOrganizationsViaFetch(token, hostSlug);
      const match = orgs.find((o) => String(o.slug || '').toLowerCase() === hostSlug);
      if (match?.id) {
        return {
          token,
          user: {
            ...primed,
            organizationId: match.id,
            organizationSlug: match.slug,
            ...(typeof match.planType === 'string' && match.planType.trim()
              ? { planType: match.planType.trim() }
              : {}),
          },
        };
      }
      return { token, user: primed };
    }

    const primed = primeStorageForHostWorkspace(token, user, hostSlug);
    const orgs = await fetchOrganizationsViaFetch(token, hostSlug);
    const match = orgs.find((o) => String(o.slug || '').toLowerCase() === hostSlug);
    if (!match?.id) {
      return { token, user: primed };
    }
    const switched = await switchWorkspaceViaFetch(token, match.id, hostSlug);
    if (switched) {
      return {
        token: switched.token,
        user: {
          ...switched.user,
          organizationId: switched.user.organizationId ?? match.id,
          organizationSlug: switched.user.organizationSlug ?? match.slug,
          planType:
            switched.user.planType ||
            (typeof match.planType === 'string' && match.planType.trim() ? match.planType.trim() : undefined),
        },
      };
    }
    return { token, user: primed };
  };

  const persistAuthAndOrganization = (user: User, token: string) => {
    const hostSlugRaw = getHostTenantSlug();
    const hostSlug =
      hostSlugRaw && !isSuperAdminTenantSlug(hostSlugRaw) ? hostSlugRaw : undefined;
    // Global/main-domain login must not attach a workspace unless the server bound one (e.g. ?tenant=).
    const orgSlug = isMainDomain
      ? user.organizationSlug
      : user.organizationSlug || hostSlug;
    const enrichedUser: User = { ...user };
    if (orgSlug) {
      enrichedUser.organizationSlug = orgSlug;
    } else {
      delete enrichedUser.organizationId;
      delete enrichedUser.organizationSlug;
      delete enrichedUser.planType;
    }
    flushSync(() => {
      login(enrichedUser, token);
      if (isMainDomain && !orgSlug) {
        localStorage.removeItem('currentOrganization');
        setOrganization(null);
        return;
      }
      if (orgSlug && enrichedUser.organizationId) {
        setOrganization({
          id: enrichedUser.organizationId,
          name: '',
          slug: orgSlug,
          planType: enrichedUser.planType || 'FREE',
        });
      }
    });
  };

  const readRememberedWorkspace = (): { id?: number; slug?: string } | null => {
    try {
      const raw = localStorage.getItem('currentOrganization');
      if (!raw) {
        return null;
      }
      const org = JSON.parse(raw);
      return {
        id: typeof org?.id === 'number' ? org.id : undefined,
        slug: typeof org?.slug === 'string' ? org.slug : undefined,
      };
    } catch {
      return null;
    }
  };

  /** Uses fetch (not axios) so a transient 401 during handoff does not clear the new session. */
  const fetchOrganizationsViaFetch = async (
    authToken: string,
    tenantSlugHint?: string | null
  ): Promise<Array<{ id?: number; slug?: string; planType?: string; role?: string }>> => {
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${authToken}` };
      if (tenantSlugHint) {
        headers['X-Tenant-Slug'] = tenantSlugHint;
      }
      const res = await fetch(`${getApiBaseUrl()}/organizations`, { headers });
      if (!res.ok) {
        return [];
      }
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('fetchOrganizationsViaFetch failed', error);
      return [];
    }
  };

  const switchWorkspaceViaFetch = async (
    authToken: string,
    organizationId: number,
    tenantSlugHint?: string | null
  ): Promise<{ token: string; user: User } | null> => {
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${authToken}` };
      if (tenantSlugHint) {
        headers['X-Tenant-Slug'] = tenantSlugHint;
      }
      const res = await fetch(`${getApiBaseUrl()}/organizations/${organizationId}/switch`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as AuthResponse;
      if (data.success && data.token && data.user) {
        return { token: data.token, user: data.user };
      }
    } catch (error) {
      console.warn('switchWorkspaceViaFetch failed', error);
    }
    return null;
  };

  const userBelongsToWorkspaceSlug = async (
    authToken: string,
    slug: string | undefined
  ): Promise<boolean> => {
    const normalized = String(slug || '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    // On tenant hosts (e.g. fly.localhost), backend org listing may require the workspace hint.
    // Without this, login can wrongly conclude "no host access" and bounce back to /login.
    const orgs = await fetchOrganizationsViaFetch(authToken, normalized);
    return orgs.some((o) => String(o.slug || '').trim().toLowerCase() === normalized);
  };

  const resolveWorkspaceSlugAfterLogin = async (
    authToken: string,
    authUser: User,
    remembered: { id?: number; slug?: string } | null
  ): Promise<{ slug?: string; token: string; user: User }> => {
    let activeToken = authToken;
    let activeUser = authUser;

    if (activeUser.organizationSlug) {
      const slugFromLogin = String(activeUser.organizationSlug).trim();
      if (slugFromLogin) {
        if (await userBelongsToWorkspaceSlug(activeToken, slugFromLogin)) {
          return { slug: slugFromLogin, token: activeToken, user: activeUser };
        }
        // Trust server-bound workspace from login when org APIs are empty/unreachable (avoids stripping org → failed redirect).
        const orgs = await fetchOrganizationsViaFetch(activeToken, slugFromLogin);
        if (orgs.length === 0) {
          return { slug: slugFromLogin, token: activeToken, user: activeUser };
        }
        activeUser = { ...activeUser };
        delete activeUser.organizationSlug;
        delete activeUser.organizationId;
      }
    }
    if (remembered?.slug) {
      if (await userBelongsToWorkspaceSlug(activeToken, remembered.slug)) {
        return { slug: remembered.slug, token: activeToken, user: activeUser };
      }
    }

    if (remembered?.id) {
      const switched = await switchWorkspaceViaFetch(activeToken, remembered.id);
      if (switched?.user.organizationSlug) {
        return {
          slug: switched.user.organizationSlug,
          token: switched.token,
          user: switched.user,
        };
      }
    }

    const apiBase = getApiBaseUrl();
    const authHeaders = { Authorization: `Bearer ${activeToken}` };
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await delay(300 * attempt);
      }

      try {
        const primaryRes = await fetch(`${apiBase}/organizations/primary`, { headers: authHeaders });
        if (primaryRes.ok) {
          const primary = await primaryRes.json();
          const slug = primary?.slug ? String(primary.slug) : undefined;
          const orgId = typeof primary?.id === 'number' ? primary.id : undefined;
          if (slug && orgId) {
            const switched = await switchWorkspaceViaFetch(activeToken, orgId);
            if (switched?.user.organizationSlug) {
              return {
                slug: switched.user.organizationSlug,
                token: switched.token,
                user: switched.user,
              };
            }
          }
          if (slug) {
            return { slug, token: activeToken, user: activeUser };
          }
        }
      } catch (error) {
        console.warn('resolveWorkspaceSlugAfterLogin primary fetch', error);
      }

      try {
        const orgsRes = await fetch(`${apiBase}/organizations`, { headers: authHeaders });
        if (orgsRes.ok) {
          const orgs = await orgsRes.json();
          if (Array.isArray(orgs) && orgs.length > 0) {
            const preferred =
              activeUser.organizationId != null
                ? orgs.find((o: { id?: number }) => o.id === activeUser.organizationId) ?? orgs[0]
                : orgs[0];
            const slug = preferred?.slug ? String(preferred.slug) : undefined;
            const orgId = typeof preferred?.id === 'number' ? preferred.id : undefined;
            if (slug && orgId) {
              const switched = await switchWorkspaceViaFetch(activeToken, orgId);
              if (switched?.user.organizationSlug) {
                return {
                  slug: switched.user.organizationSlug,
                  token: switched.token,
                  user: switched.user,
                };
              }
            }
            if (slug) {
              return { slug, token: activeToken, user: activeUser };
            }
          }
          return { token: activeToken, user: activeUser };
        }
      } catch (error) {
        console.warn('resolveWorkspaceSlugAfterLogin orgs fetch', error);
      }
    }

    return { token: activeToken, user: activeUser };
  };

  /** Main-domain login: exchange JWT and open the app on the tenant host (dashboard), not the marketing site. */
  const handoffMainDomainSessionToWorkspace = async (
    token: string,
    user: User,
    redirectPath: string,
    remembered: { id?: number; slug?: string } | null
  ): Promise<void> => {
    if (isSuperAdminUser(user)) {
      try {
        const exchangeResponse = await apiService.createExchangeToken({
          token,
          organizationSlug: SUPER_ADMIN_TENANT_SLUG,
        });
        redirectToTenant(
          SUPER_ADMIN_TENANT_SLUG,
          buildTenantExchangeHandoffPath(redirectPath),
          exchangeResponse.exchangeToken
        );
      } catch (error) {
        console.error('Error creating super-admin exchange token:', error);
        failMainDomainHandoff('Could not open the admin workspace. Please try signing in again.');
      }
      return;
    }

    const resolved = await resolveWorkspaceSlugAfterLogin(token, user, remembered);
    if (resolved.token !== token || resolved.user !== user) {
      persistAuthAndOrganization(resolved.user, resolved.token);
    }

    const workspaceSlug = resolved.slug;
    if (!workspaceSlug) {
      failMainDomainHandoff(
        'No workspace is linked to this account yet. Create or join an organization, then sign in again.'
      );
      return;
    }

    try {
      const exchangeResponse = await apiService.createExchangeToken({
        token: resolved.token,
        organizationSlug: workspaceSlug,
      });
      redirectToTenant(
        workspaceSlug,
        buildTenantExchangeHandoffPath(redirectPath),
        exchangeResponse.exchangeToken
      );
    } catch (error) {
      console.error('Error creating exchange token:', error);
      failMainDomainHandoff('Could not open your workspace. Please try signing in again.');
    }
  };

  /**
   * After successful login (email + password + captcha + OTP): send the user straight to the right place.
   * On the marketing/main host, always uses exchange token → tenant subdomain (no delay).
   */
  const mapMeRoleToAuthRole = (meRole: string): string => {
    switch (meRole.toUpperCase()) {
      case 'ADMIN_PM':
        return 'ADMIN_PROJECT_MANAGER';
      case 'ADMIN':
        return 'ADMIN';
      case 'PROJECT_MANAGER':
        return 'PROJECT_MANAGER';
      case 'MEMBER':
        return 'MEMBER';
      default:
        return meRole;
    }
  };

  const refreshUserRoleFromMe = async (token: string, user: User): Promise<User> => {
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const hostSlug = getHostTenantSlug();
      if (hostSlug) {
        headers['X-Tenant-Slug'] = hostSlug;
      }
      const res = await fetch(`${getApiBaseUrl()}/user/me`, { headers });
      if (!res.ok) {
        return user;
      }
      const body = await res.json();
      if (typeof body?.role === 'string' && body.role.trim()) {
        return { ...user, role: mapMeRoleToAuthRole(body.role) };
      }
    } catch (error) {
      console.warn('Could not refresh role from /user/me', error);
    }
    return user;
  };

  const markTenantHostValidated = () => {
    try {
      sessionStorage.setItem(`tenant_valid_${window.location.hostname}`, 'true');
    } catch {
      /* ignore */
    }
  };

  const enrichUserFromLogin = (user: User): User => {
    const next: User = { ...user };
    if (next.role) {
      next.role = mapMeRoleToAuthRole(next.role);
    }
    const hostSlug = getHostTenantSlug();
    if (hostSlug && !isSuperAdminTenantSlug(hostSlug)) {
      if (!next.organizationSlug) {
        next.organizationSlug = hostSlug;
      }
    }
    return next;
  };

  const redirectAfterLogin = async (data: AuthResponse) => {
    if (!data.user || !data.token) {
      return;
    }
    const roleRefreshedUser = enrichUserFromLogin(
      await refreshUserRoleFromMe(data.token, data.user)
    );
    // Do not clear token/user here — clearing before persist caused axios 401 → /login loops on tenant URLs.
    if (!postLoginRedirectRef.current) {
      if (shouldUseTenantHandoff) {
        markPostLoginHandoff();
      } else {
        beginPostLoginRedirect();
      }
    }
    const params = new URLSearchParams(window.location.search);
    captureOnboardingFromUrl();
    const returnUrl = params.get('returnUrl');
    let redirectPath = data.requirePasswordChange
      ? '/profile?changePassword=required'
      : resolvePostLoginPath(returnUrl, roleRefreshedUser);
    if (isOnboardingSessionActive() && !data.requirePasswordChange) {
      redirectPath = '/proxy-settings';
    }

    if (shouldUseTenantHandoff) {
      const workspaceSlug = String(roleRefreshedUser.organizationSlug || '').trim();
      if (workspaceSlug) {
        persistAuthAndOrganization(roleRefreshedUser, data.token);
        try {
          const exchangeResponse = await apiService.createExchangeToken({
            token: data.token,
            organizationSlug: workspaceSlug,
          });
          redirectToTenant(
            workspaceSlug,
            buildTenantExchangeHandoffPath(redirectPath),
            exchangeResponse.exchangeToken
          );
          return;
        } catch (error) {
          console.error('Direct workspace handoff failed, retrying resolver:', error);
        }
      }
      const remembered = readRememberedWorkspace();
      persistAuthAndOrganization(roleRefreshedUser, data.token);
      await handoffMainDomainSessionToWorkspace(data.token, roleRefreshedUser, redirectPath, remembered);
      return;
    }

    // Tenant URL: only bind to this host when the account belongs here; otherwise hand off to the user's workspace.
    const hostSlug = getHostTenantSlug();
    if (!shouldUseTenantHandoff && hostSlug && !isSuperAdminTenantSlug(hostSlug)) {
      const loginUser: User = {
        ...roleRefreshedUser,
        organizationSlug: roleRefreshedUser.organizationSlug || hostSlug,
      };
      const loginBoundToHost =
        workspaceSlugMatchesHost(loginUser, hostSlug) && Boolean(loginUser.organizationId);
      if (loginBoundToHost) {
        writeTenantSessionToStorage(loginUser, data.token);
        markTenantHostValidated();
        replaceLocationToAppPath(redirectPath);
        return;
      }
      // Persist JWT before async bind so axios calls during handoff keep Authorization (avoids 401 → /login).
      primeStorageForHostWorkspace(data.token, loginUser, hostSlug);
      try {
        let finalToken = data.token;
        let finalUser = loginUser;
        if (!loginBoundToHost) {
          const bound = await bindWorkspaceSessionIfNeeded(data.token, loginUser);
          finalToken = bound.token;
          finalUser = bound.user;
        }
        const boundToHost =
          workspaceSlugMatchesHost(finalUser, hostSlug) && Boolean(finalUser.organizationId);
        if (!boundToHost) {
          await handoffMainDomainSessionToWorkspace(finalToken, finalUser, redirectPath, null);
          return;
        }
        writeTenantSessionToStorage(finalUser, finalToken);
        markTenantHostValidated();
      } catch (error) {
        console.error('Error binding workspace session:', error);
        const fallbackBound =
          workspaceSlugMatchesHost(loginUser, hostSlug) && Boolean(loginUser.organizationId);
        if (!fallbackBound) {
          await handoffMainDomainSessionToWorkspace(data.token, roleRefreshedUser, redirectPath, null);
          return;
        }
        writeTenantSessionToStorage(loginUser, data.token);
        markTenantHostValidated();
      }
      replaceLocationToAppPath(redirectPath);
      return;
    }

    persistAuthAndOrganization(roleRefreshedUser, data.token);
    replaceLocationToAppPath(redirectPath);
  };

  const clearStaleAuthStorage = () => {
    clearAuthAndTenantStorage();
  };

  // Extract exchange token from URL if present (cross-subdomain authentication)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    captureOnboardingFromUrl();
    const exchangeToken = params.get('exchangeToken');
    const returnUrl = params.get('returnUrl');

    if (!exchangeToken) {
      return;
    }

    const dedupeKey = `${window.location.hostname}:${exchangeToken}`;
    const exchangeDoneKey = `exchange_done_${dedupeKey}`;
    try {
      if (sessionStorage.getItem(exchangeDoneKey) === '1') {
        return;
      }
    } catch {
      /* ignore */
    }

    let exchangePromise = exchangeTokenInflight.get(dedupeKey);
    if (!exchangePromise) {
      clearStaleAuthStorage();
      exchangePromise = apiService.exchangeToken(exchangeToken);
      exchangeTokenInflight.set(dedupeKey, exchangePromise);
      exchangePromise.finally(() => {
        exchangeTokenInflight.delete(dedupeKey);
      });
    }

    exchangePromise
      .then(async (data) => {
        if (data.success && data.token && data.user) {
          let finalToken = data.token;
          let finalUser = enrichUserFromLogin(data.user);
          const hostSlugForExchange = getHostTenantSlug();
          if (
            hostSlugForExchange &&
            !isSuperAdminTenantSlug(hostSlugForExchange)
          ) {
            primeStorageForHostWorkspace(
              data.token,
              {
                ...data.user,
                organizationSlug: data.user.organizationSlug || hostSlugForExchange,
              },
              hostSlugForExchange
            );
          }
          try {
            const bound = await bindWorkspaceSessionIfNeeded(data.token, data.user);
            finalToken = bound.token;
            finalUser = bound.user;
          } catch (bindError) {
            console.error('Error binding workspace after exchange:', bindError);
          }
          const hostSlug = getHostTenantSlug();
          const resolvedSlug = String(finalUser.organizationSlug || '').trim().toLowerCase();
          if (
            hostSlug &&
            !isSuperAdminTenantSlug(hostSlug) &&
            resolvedSlug &&
            resolvedSlug !== hostSlug
          ) {
            const redirectPath = isOnboardingSessionActive()
              ? '/proxy-settings'
              : resolvePostLoginPath(returnUrl, finalUser);
            await handoffMainDomainSessionToWorkspace(finalToken, finalUser, redirectPath, null);
            return;
          }
          if (hostSlug && !isSuperAdminTenantSlug(hostSlug)) {
            writeTenantSessionToStorage(finalUser, finalToken);
            markTenantHostValidated();
          } else {
            persistAuthAndOrganization(finalUser, finalToken);
          }

          try {
            sessionStorage.setItem(exchangeDoneKey, '1');
          } catch {
            /* ignore */
          }
          const redirectPath = isOnboardingSessionActive()
            ? '/proxy-settings'
            : resolvePostLoginPath(returnUrl, finalUser);
          replaceLocationToAppPath(redirectPath);
        } else {
          postLoginRedirectRef.current = false;
          console.error('Exchange token failed:', data.message);
          setIsProcessingAuth(false);
          setMessage({ text: 'Authentication failed. Please login again.', type: 'error' });
        }
      })
      .catch((error) => {
        postLoginRedirectRef.current = false;
        console.error('Error exchanging token:', error);
        setIsProcessingAuth(false);
        setMessage({ text: 'Authentication failed. Please login again.', type: 'error' });
      });
    // Run once per page load when exchangeToken is present (avoid StrictMode remount clearing a fresh session).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect if user was redirected due to SESSION_REPLACED
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectedAtRaw = sessionStorage.getItem('sessionReplacedRedirectedAt');
    const showSessionReplacedNotice = sessionStorage.getItem('sessionReplacedNotice') === '1';
    const redirectedAt = redirectedAtRaw ? Number(redirectedAtRaw) : NaN;
    const recentlyRedirectedByInterceptor =
      Number.isFinite(redirectedAt) && Math.max(0, Date.now() - redirectedAt) <= 15000;
    if (params.get('sessionReplaced') === '1') {
      const localDev = isLocalDevHostname(window.location.hostname);
      // After Sign In, a transient 401 during handoff can bounce here — not a real cross-device sign-out.
      const suppressFalseSessionReplacedNotice = isRecentLoginAttempt();
      if (
        !localDev &&
        recentlyRedirectedByInterceptor &&
        showSessionReplacedNotice &&
        !suppressFalseSessionReplacedNotice
      ) {
        setMessage({
          text: 'You were signed out because this account was used to sign in from another browser or device.',
          type: 'error',
        });
      }
      sessionStorage.removeItem('sessionReplacedRedirectedAt');
      sessionStorage.removeItem('sessionReplacedNotice');
      sessionStorage.removeItem(LOGIN_ATTEMPT_AT_KEY);
      // Clean the query param so message doesn't persist forever
      params.delete('sessionReplaced');
      const cleanPath = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', cleanPath);
    } else if (!recentlyRedirectedByInterceptor) {
      // Clear stale marker so it cannot trigger false notice later.
      sessionStorage.removeItem('sessionReplacedRedirectedAt');
      sessionStorage.removeItem('sessionReplacedNotice');
    }
  }, []);

  useEffect(() => {
    captureOnboardingFromUrl();
  }, []);

  // If user is already authenticated on a tenant host, redirect to the app (not on marketing /login — Sign In must show the form).
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isExchangeHandoffInProgress() && !isAuthenticated) {
      if (!postLoginRedirectRef.current) {
        setIsProcessingAuth(false);
      }
      return;
    }

    if (
      !isAuthenticated ||
      isMainDomain ||
      isProcessingAuth ||
      postLoginRedirectRef.current ||
      isExchangeHandoffInProgress() ||
      autoRedirectInFlightRef.current
    ) {
      return;
    }

    autoRedirectInFlightRef.current = true;
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    let redirectPath = '/dashboard';
    let parsedUser: User | null = null;
    if (storedUser) {
      try {
        parsedUser = JSON.parse(storedUser) as User;
        redirectPath = getRedirectPath(parsedUser);
      } catch {
        /* use default */
      }
    }

    void (async () => {
      const hostSlug = getHostTenantSlug();
      if (!storedToken || !hostSlug || isSuperAdminTenantSlug(hostSlug)) {
        replaceLocationToAppPath(redirectPath);
        return;
      }

      // If local session already matches this host, avoid extra network checks that can transiently fail and bounce to /login.
      const sessionSlug = String(parsedUser?.organizationSlug || '').trim().toLowerCase();
      if (sessionSlug && sessionSlug === hostSlug) {
        replaceLocationToAppPath(redirectPath);
        return;
      }

      const belongsOnHost = await userBelongsToWorkspaceSlug(storedToken, hostSlug);
      if (!belongsOnHost && parsedUser) {
        await handoffMainDomainSessionToWorkspace(storedToken, parsedUser, redirectPath, null);
        return;
      }
      if (!belongsOnHost) {
        // Do not clear auth on a soft membership check miss; that can create a login loop on temporary API/network issues.
        replaceLocationToAppPath(redirectPath);
        return;
      }
      replaceLocationToAppPath(redirectPath);
    })();
  }, [authLoading, isAuthenticated, isMainDomain, isProcessingAuth]);

  const drawCaptcha = useCallback((value: string) => {
    const canvas = captchaCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 140;
    canvas.height = 44;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f4f6fb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 22px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#1f2937';
    ctx.fillText(value, canvas.width / 2, canvas.height / 2);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.28)`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
  }, []);

  const generateCaptcha = useCallback((clearCaptchaError = true) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(code);
    setCaptchaInput('');
    if (clearCaptchaError) {
      setErrors((prev) => ({ ...prev, captcha: undefined }));
    }
    drawCaptcha(code);
  }, [drawCaptcha]);

  const sendOtpToEmail = async () => {
    if (otpSendInFlightRef.current) return;
    if (!formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Email is required before sending OTP' }));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      setErrors((prev) => ({ ...prev, email: 'Email is invalid' }));
      return;
    }

    otpSendInFlightRef.current = true;
    setIsLoading(true);
    setMessage(null);
    try {
      const response = await apiService.sendAuthOtp({
        email: formData.email.trim().toLowerCase(),
        purpose: 'LOGIN',
        password: formData.password,
      });
      if (response.success) {
        setOtpSent(true);
        setOtpInput('');
        setMessage({ text: response.message || 'OTP sent to your email', type: 'success' });
      } else {
        setMessage({ text: response.message || 'Failed to send OTP', type: 'error' });
      }
    } catch (error: any) {
      const data = error.response?.data;
      const apiMessage =
        (typeof data === 'object' && data !== null && typeof data.message === 'string'
          ? data.message
          : null) ||
        (typeof data === 'object' && data !== null && typeof data.error === 'string'
          ? data.error
          : null) ||
        (typeof data === 'string' ? data : null);
      const status = error.response?.status;
      const fallback =
        status === 502
          ? 'Unable to send OTP email. Please try again in a moment.'
          : status === 400
            ? 'Could not send OTP. Check your email or password.'
            : error.message || 'Failed to send OTP. Check your email address and try again.';
      setMessage({
        text: apiMessage || fallback,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      otpSendInFlightRef.current = false;
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  // Show success message after password reset redirect from forgot-password flow.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(PASSWORD_RESET_LOGIN_QUERY) === '1') {
        setMessage({
          text: 'Password reset successfully. Sign in with your new password and verify the OTP sent to your email.',
          type: 'success',
        });
        params.delete(PASSWORD_RESET_LOGIN_QUERY);
        const clean =
          window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', clean);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (captchaText) drawCaptcha(captchaText);
  }, [captchaText]);

  const validateForm = (): { isValid: boolean; fieldErrors: LoginErrors } => {
    const newErrors: LoginErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!captchaInput.trim()) {
      newErrors.captcha = 'Security code is required';
    } else if (
      captchaInput.trim() !== captchaText
      && !(testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha)
    ) {
      newErrors.captcha = 'Invalid captcha (case-sensitive)';
    }

    if (!isSuperAdminEmail) {
      if (!otpInput.trim()) {
        newErrors.otp = 'OTP is required';
      } else if (!/^\d{6}$/.test(otpInput.trim())) {
        newErrors.otp = 'OTP must be 6 digits';
      } else if (/^(\d)\1{5}$/.test(otpInput.trim())) {
        newErrors.otp = 'OTP cannot contain all same digits';
      }
      if (!otpSent && !(testAuthFlowEnabled && otpInput.trim() === testStaticOtp)) {
        newErrors.otp = 'Please send OTP to your email first';
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, fieldErrors: newErrors };
  };

  const validateCredentialsAndCaptcha = (): { isValid: boolean; fieldErrors: LoginErrors } => {
    const newErrors: LoginErrors = {};

    const emailTrimmed = formData.email.trim();
    if (!emailTrimmed) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Za-z0-9+_.-]+@(.+)$/.test(emailTrimmed)) {
      newErrors.email = 'Email is invalid'; 
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!captchaInput.trim()) {
      newErrors.captcha = 'Security code is required';
    } else if (
      captchaInput.trim() !== captchaText
      && !(testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha)
    ) {
      newErrors.captcha = 'Invalid captcha (case-sensitive)';
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, fieldErrors: newErrors };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/\s+/g, '');
    if (name === 'email' || name === 'password') {
      setOtpSent(false);
      setOtpInput('');
    }
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof LoginForm]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginSubmitInFlightRef.current) return;
    loginSubmitInFlightRef.current = true;

    try {
      if (!isSuperAdminEmail && !otpSent) {
        const { isValid, fieldErrors } = validateCredentialsAndCaptcha();
        if (!isValid) {
          if (fieldErrors.captcha?.toLowerCase().includes('invalid')) generateCaptcha(false);
          return;
        }
        if (testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha) {
          setOtpSent(true);
          setMessage({
            text: `OTP has been sent to your email address`,
            type: 'success',
          });
          return;
        }
        await sendOtpToEmail();
        return;
      }

      const { isValid, fieldErrors } = validateForm();
      if (!isValid) {
        if (fieldErrors.captcha?.toLowerCase().includes('invalid')) generateCaptcha(false);
        return;
      }

      setIsLoading(true);
      setMessage(null);
      markLoginAttempt();

      const completeLogin = async (data: AuthResponse) => {
        if (!data.user || !data.token) {
          setMessage({ text: data.message || 'Login incomplete.', type: 'error' });
          return;
        }
        if (shouldUseTenantHandoff) {
          markPostLoginHandoff();
        } else {
          beginPostLoginRedirect();
        }
        setMessage({ text: 'Login successful! Redirecting…', type: 'success' });
        await redirectAfterLogin(data);
      };

      try {
        const data = await apiService.login({
          ...formData,
          otp: isSuperAdminEmail ? '' : otpInput.trim(),
        });

        if (data.success) {
          await completeLogin(data);
        } else if (data.alreadyLoggedIn) {
          setShowSessionModal(true);
          setMessage({
            text: data.message || 'You are already signed in on another device. Continue to replace that session.',
            type: 'error',
          });
        } else {
          setMessage({ text: data.message || 'Login failed', type: 'error' });
        }
      } catch (error: any) {
        const backendData = error.response?.data;
        if (backendData?.alreadyLoggedIn) {
          setShowSessionModal(true);
          setMessage({
            text: backendData?.message || 'You are already signed in on another device. Continue to replace that session.',
            type: 'error',
          });
        } else {
          setMessage({
            text: backendData?.message || 'Network error. Please try again.',
            type: 'error',
          });
        }
      } finally {
        if (!postLoginRedirectRef.current) {
          setIsLoading(false);
        }
      }
    } finally {
      loginSubmitInFlightRef.current = false;
    }
  };

  // Handoff in progress: hide login chrome without a separate "Signing you in" screen (dashboard loads next).
  if (isProcessingAuth) {
    return null;
  }

  return (
    <AuthPageTemplate
      variant="login"
      leftImageSrc="/login-security-illustration.png"
      leftImageAlt="Secure login illustration"
      className={otpSent && !isSuperAdminEmail ? 'otp-expanded' : ''}
      style={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        fontSize: '0.8rem',
        lineHeight: 1.35,
        zoom: 0.9,
      }}
    >
      {showSessionModal && (
        <>
          <div className="session-conflict-overlay" aria-hidden="true" />
          <div className="session-conflict-banner-float">
            <div className="session-conflict-banner">
              <div className="session-conflict-banner-content">
            <h3 className="session-conflict-banner-title">Already logged in</h3>
            <p className="session-conflict-banner-message">
              You are already logged in from another browser or device. Do you want to close that session and continue logging in here?
            </p>
            <div className="session-conflict-banner-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setShowSessionModal(false);
                  if (!sessionReplacedNotice) {
                    setMessage({ text: 'Login cancelled. Your other session is still active.', type: 'error' });
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="auth-button"
                onClick={async () => {
                  setIsLoading(true);
                  markLoginAttempt();
                  try {
                    const data = await apiService.forceLogin({
                      ...formData,
                      otp: isSuperAdminEmail ? '' : otpInput.trim(),
                    });
                    if (data.success && data.user && data.token) {
                      setShowSessionModal(false);
                      if (shouldUseTenantHandoff) {
                        markPostLoginHandoff();
                      } else {
                        beginPostLoginRedirect();
                      }
                      await redirectAfterLogin(data);
                    } else {
                      setMessage({ text: data.message || 'Login failed', type: 'error' });
                    }
                  } catch (error: any) {
                    const backendData = error.response?.data;
                    setMessage({
                      text: backendData?.message || 'Network error. Please try again.',
                      type: 'error'
                    });
                  } finally {
                    if (!postLoginRedirectRef.current) {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                Continue
              </button>
            </div>
          </div>
          </div>
          </div>
        </>
      )}

      <div className="auth-card login-auth-card">
        <div className="auth-logo-wrapper">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <img
              src="/crud-expert-sign-in-icon.png"
              alt="CRUD Expert"
              style={{
                width: 36,
                height: 43,
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
                backgroundColor: '#fff',
              }}
            />
            <span
              style={{
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: 22,
                fontWeight: 700,
                lineHeight: 1.15,
                color: '#060a33',
                letterSpacing: 0.2,
              }}
            >
              CRUD EXPERT
            </span>
          </div>
        </div>
        <h2>Welcome Back</h2>
        <p className="auth-subtitle">Sign in to your account</p>
        
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label htmlFor="password">Password</label>
              
            </div>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              hasError={!!errors.password}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
              maxLength={PASSWORD_MAX_LENGTH}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
            <small className="password-requirements-hint">{PASSWORD_REQUIREMENTS_HINT}</small>
          </div>

          {!isSuperAdminEmail && (
          <div className="form-group">
            <label htmlFor="captchaInput">Security Code</label>
            <div className="security-code-row">
              <canvas
                ref={captchaCanvasRef}
                onClick={() => generateCaptcha()}
                aria-label="Captcha challenge"
                className="security-code-canvas"
              />
              <button
                type="button"
                onClick={() => generateCaptcha()}
                disabled={isLoading}
                aria-label="Refresh security code"
                className="security-code-refresh"
              >
                ↻
              </button>
              <input
                type="text"
                id="captchaInput"
                name="captchaInput"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value.replace(/\s+/g, ''));
                  if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: undefined }));
                }}
                className={`security-code-input ${errors.captcha ? 'error' : ''}`}
                placeholder="Enter security code"
                maxLength={5}
              />
            </div>
            {errors.captcha && <span className="error-text">{errors.captcha}</span>}
          </div>
          )}

          {otpSent && !isSuperAdminEmail && (
            <div className="form-group">
              <label htmlFor="otpInput">OTP Verification</label>
              <input
                type="text"
                id="otpInput"
                name="otpInput"
                value={otpInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s+/g, '').replace(/\D/g, '').slice(0, 6);
                  setOtpInput(value);
                  if (errors.otp) setErrors((prev) => ({ ...prev, otp: undefined }));
                }}
                className={errors.otp ? 'error' : ''}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
              />
              {errors.otp && <span className="error-text">{errors.otp}</span>}
              <div style={{ marginTop: '8px', textAlign: 'right' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={sendOtpToEmail}
                  disabled={isLoading}
                  style={{ fontSize: '14px' }}
                >
                  Resend OTP
                </button>
              </div>
            </div>
          )}

          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading
              ? (isSuperAdminEmail ? 'Signing In...' : (otpSent ? 'Verifying...' : 'Sending OTP...'))
              : (isSuperAdminEmail ? 'Sign In' : (otpSent ? 'Verify OTP & Sign In' : 'Sign In'))
            }
          </button>
          <div className="forgot-password-wrapper">
  <button
    type="button"
    className="link-button forgot-password-link"
    onClick={() => {
      navigate('/forgot-password');
    }}
  >
    Forgot password?
  </button>
</div>
        </form>


        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <button 
              className="link-button" 
              onClick={() => {
                // On app/main domain, go to global signup page; on tenant domain, use tenant-specific register route
                if (isMainDomain) {
                  navigate('/signup');
                } else {
                  navigate('/register');
                }
              }}
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </AuthPageTemplate>
  );
};

export default Login; 